import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { getRouteApi } from '@tanstack/react-router';
import jwt_decode from 'jwt-decode';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';

import Finished from '../components/Finished';
import type { SchemaJWT } from '../schemas/jwt';
import { schemaJWT } from '../schemas/jwt';
import type { SchemaResetPassword } from '../schemas/resetPassword';
import { schemaResetPassword } from '../schemas/resetPassword';
import { APICommunicator } from '~/api/APICommunicator';
import NotFound from '~/components/error/NotFound';
import TreatButton from '~/components/TreatButton';

const clsInput =
    'bg-white form-input border-transparent w-full placeholder:text-dark text-grey text-xs px-3 py-2.5 mb-4';

const ResetPasswordRoute = () => {
    const { t } = useTranslation();
    const [isFinished, setIsFinished] = useState(false);
    const router = getRouteApi('/_auth/reset/password/');
    const { jwt } = router.useSearch();

    const { handleSubmit, register } = useForm<SchemaResetPassword>({
        defaultValues: {
            password: '',
            passwordRepeat: '',
        },
        resolver: zodResolver(schemaResetPassword),
    });

    if (typeof jwt !== 'string' || jwt === '') {
        throw new Error('JWT not found');
    }

    let decodedJwt: SchemaJWT;
    try {
        decodedJwt = schemaJWT.parse(jwt_decode(jwt));
    } catch {
        return <NotFound />;
    }

    const onSubmit: SubmitHandler<SchemaResetPassword> = async ({ password }) => {
        const response = await APICommunicator.resetResource({
            config: {
                body: JSON.stringify({ password }),
                method: 'POST',
            },
            path: '/reset/password',
            schema: z.object({
                success: z.boolean(),
            }),
        });
        if (response.success) {
            setIsFinished(true);
            return;
        }

        toast.error(response.error, { theme: 'colored' });
    };

    const onError: SubmitErrorHandler<SchemaResetPassword> = (error) => {
        if (error.password) {
            toast.error(t('LOGIN.PASSWORD_NOT_VALID'), { theme: 'colored' });
        }
        if ('both' in error && error.both) {
            toast.error(t('LOGIN.PASSWORD_DOES_NOT_MATCH'), { theme: 'colored' });
        }
    };

    if (!decodedJwt.id || decodedJwt.type !== 'password' || decodedJwt.exp * 1000 < Date.now()) return <NotFound />;

    if (isFinished) return <Finished>{t('users.reset.password.FINISH_INFO')}</Finished>;

    return (
        <div className="flex items-center justify-center w-full min-h-screen p-4">
            <div className="w-full max-w-[420px] p-4 mx-auto bg-grey-light">
                <h3 className="mb-4 text-3xl font-bold text-center text-blue">{t('PAGES.LOGIN')}</h3>
                <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col">
                    <input
                        type="password"
                        className={clsInput}
                        placeholder={t('FORM_FIELDS.PASSWORD')}
                        {...register('password')}
                        aria-label={t('FORM_FIELDS.PASSWORD')}
                        required
                    />
                    <input
                        type="password"
                        className={clsInput}
                        placeholder={t('FORM_FIELDS.PASSWORD_REPEAT')}
                        {...register('passwordRepeat')}
                        aria-label={t('FORM_FIELDS.PASSWORD_REPEAT')}
                        required
                    />
                    <div className="flex items-center justify-end space-x-5">
                        <TreatButton className="text-xs font-semibold bg-blue py-2 px-3.5 text-white">
                            {t('users.reset.password.SUBMIT')}
                        </TreatButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPasswordRoute;
